import * as THREE from 'three';
import type { Point } from '../../../shared/game/world-types.js';
import { PING_SECONDS, type PingKind, type PingMark } from '../../../shared/network/pings.js';
import { hudIcons } from '../../presentation/icons.js';
import { PING_GILT, PING_LOOKS, PING_SILVER } from '../../presentation/ping-looks.js';
import { blankFace, canDraw, glyphCanvas } from '../hud/hud-materials.js';
import { groundDecal, groundUi } from '../environment/ground-decal.js';

/** Ripples sent out from the ring, and the seconds between them. */
const RIPPLES = 3, RIPPLE_GAP = .55, RIPPLE_LIFE = 1.1;
/** Gilt motes that drift up from the ring as it lands. */
const MOTES = 14;
const RING = .6;

/** The meshes of one ping, made once and sent out again for later pings rather than built and freed each time. */
interface Parts {
  group: THREE.Group; ring: THREE.Mesh; ripples: THREE.Mesh[]; glyph: THREE.Sprite;
  motes: THREE.Points<THREE.BufferGeometry, THREE.PointsMaterial>; drift: Float32Array;
}
interface Live extends Parts { ping: PingMark; age: number; at: THREE.Vector3 }

/** Each kind's glyph, inked in its colour with a dark rim so it reads on grass, sand, snow and parchment alike; kept once drawn. */
const glyphs = new Map<PingKind, Promise<HTMLCanvasElement | null>>(), drawn = new Map<PingKind, HTMLCanvasElement>();
function glyphImage(kind: PingKind): Promise<HTMLCanvasElement | null> {
  let made = glyphs.get(kind);
  if (made) return made;
  made = (async () => {
    if (!canDraw()) return null;
    const shape = await glyphCanvas(hudIcons[PING_LOOKS[kind].icon], 40), size = shape.width;
    const tint = (color: string) => {
      const canvas = document.createElement('canvas'); canvas.width = canvas.height = size;
      const context = canvas.getContext('2d')!; context.drawImage(shape, 0, 0);
      context.globalCompositeOperation = 'source-in'; context.fillStyle = color; context.fillRect(0, 0, size, size);
      return canvas;
    };
    const ink = tint(`#${new THREE.Color(PING_LOOKS[kind].color).getHexString()}`), rim = tint('#1a1008');
    const canvas = document.createElement('canvas'); canvas.width = canvas.height = size;
    const context = canvas.getContext('2d')!;
    for (let k = 0; k < 12; k++) { const a = k / 12 * Math.PI * 2; context.drawImage(rim, Math.cos(a) * 7, Math.sin(a) * 7); }
    context.drawImage(ink, 0, 0);
    drawn.set(kind, canvas);
    return canvas;
  })().catch(() => null);
  glyphs.set(kind, made);
  return made;
}

/**
 * Pings in the world: a ring set on the ground in the pinging player's colour (gilt for this player, silver for the other), which
 * lands with a small bounce and sends out ripples in its kind's colour, gilt motes rising off it, and the kind's glyph hanging above
 * it. A ping on a unit follows it while it can be seen. Presentation only; nothing here touches the rules.
 */
export class PingMarks {
  private live: Live[] = [];
  private spare: Parts[] = [];
  /** The parts built to warm the shaders, until `warmed` hands them to the pool. */
  private warming: Parts | null = null;
  private ringGeometry = new THREE.RingGeometry(RING * .82, RING, 48);
  /** Each kind's glyph as a texture, uploaded once and shared by every ping of that kind. */
  private textures = new Map<PingKind, THREE.CanvasTexture>();

  constructor(private scene: THREE.Scene, private ground: (x: number, z: number) => number,
    private unit: (id: string) => THREE.Object3D | null, private conform: (mesh: THREE.Mesh) => void) {}

  /**
   * The meshes of a ping in the colours of none: a ring, three ripples, a glyph sprite (wearing a blank map, so its glyph arriving
   * links nothing) and a puff of motes. The ripples and the ring are separate scene children, as they stand in play.
   */
  private build(): Parts {
    const group = new THREE.Group();
    const ring = groundUi(groundDecal(new THREE.Mesh(this.ringGeometry.clone(), new THREE.MeshBasicMaterial({ color: PING_GILT, side: THREE.DoubleSide, opacity: 0 }))));
    ring.rotation.x = -Math.PI / 2;
    const ripples = Array.from({ length: RIPPLES }, () => {
      const ripple = groundUi(groundDecal(new THREE.Mesh(new THREE.RingGeometry(RING * .92, RING, 48), new THREE.MeshBasicMaterial({ color: 0xffffff, side: THREE.DoubleSide, blending: THREE.AdditiveBlending, opacity: 0 }))));
      ripple.rotation.x = -Math.PI / 2; return ripple;
    });
    const glyph = new THREE.Sprite(new THREE.SpriteMaterial({ map: blankFace().map, transparent: true, depthWrite: false, opacity: 0 }));
    glyph.scale.setScalar(.75); glyph.visible = false; glyph.renderOrder = 10;
    const geometry = new THREE.BufferGeometry(); geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(MOTES * 3), 3));
    const motes = new THREE.Points(geometry, new THREE.PointsMaterial({ size: 4, sizeAttenuation: false, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, opacity: 0 }));
    group.add(groundUi(glyph), groundUi(motes));
    return { group, ring, ripples, glyph, motes, drift: new Float32Array(MOTES * 3) };
  }

  /** One of each ping part for the warm-up pass to stand under the scene, so the first ping links no shader. Hand it back with `warmed`. */
  warmup(): THREE.Object3D {
    const parts = this.warming ??= this.build(), warm = new THREE.Group();
    parts.glyph.visible = true;
    warm.name = 'ping-shader-warmup'; warm.add(parts.group, parts.ring, ...parts.ripples);
    return warm;
  }
  /** The warm-up pass is over: its parts become the first pooled ones. */
  warmed() {
    const parts = this.warming;
    if (!parts) return;
    this.warming = null; for (const piece of [parts.group, parts.ring, ...parts.ripples]) piece.removeFromParent();
    parts.glyph.visible = false; this.spare.push(parts);
  }

  add(ping: PingMark) {
    const look = PING_LOOKS[ping.kind], parts = this.spare.pop() ?? this.build();
    const { group, ring, ripples, glyph, motes, drift } = parts;
    (ring.material as THREE.MeshBasicMaterial).color.setHex(ping.mine ? PING_GILT : PING_SILVER);
    for (const ripple of ripples) (ripple.material as THREE.MeshBasicMaterial).color.setHex(look.color);
    motes.material.color.setHex(look.color);
    glyph.material.map = blankFace().map; glyph.visible = false;
    const live: Live = { ...parts, ping, age: 0, at: new THREE.Vector3(ping.at.x, this.ground(ping.at.x, ping.at.z), ping.at.z) };
    void glyphImage(ping.kind).then(image => {
      if (!image || !this.live.includes(live)) return;
      glyph.material.map = this.texture(ping.kind, image); glyph.visible = true;
    });
    for (let i = 0; i < MOTES; i++) {
      const a = Math.random() * Math.PI * 2, r = RING * (.6 + Math.random() * .5);
      drift[i * 3] = Math.cos(a) * r; drift[i * 3 + 1] = .5 + Math.random() * 1.1; drift[i * 3 + 2] = Math.sin(a) * r;
    }
    this.scene.add(group, ring, ...ripples);
    this.live.push(live);
  }
  private texture(kind: PingKind, image: HTMLCanvasElement) {
    let texture = this.textures.get(kind);
    if (!texture) { texture = new THREE.CanvasTexture(image); texture.colorSpace = THREE.SRGBColorSpace; this.textures.set(kind, texture); }
    return texture;
  }

  /** Hands every live ping's ground marks to `take`, for drawing them apart from the world (the picture filters' crisp ground UI). */
  groundUi(take: (object: THREE.Object3D) => void) {
    for (const mark of this.live) { take(mark.group); take(mark.ring); for (const ripple of mark.ripples) take(ripple); }
  }

  /** Advances every ping by `dt` seconds of real time. */
  update(dt: number) {
    for (const mark of this.live) {
      mark.age += dt;
      const t = mark.age, fade = Math.min(1, t / .12, (PING_SECONDS - t) / .6);
      // A ping on a unit follows it while it is seen, and stays where it was last seen otherwise.
      const unit = mark.ping.targetId ? this.unit(mark.ping.targetId) : null;
      if (unit?.visible) mark.at.copy(unit.position);
      const { x, z } = mark.at, y = this.ground(x, z);
      const land = t < .3 ? easeOutBack(t / .3) : 1;
      mark.ring.position.set(x, y + .07, z); mark.ring.scale.setScalar(Math.max(.01, land)); (mark.ring.material as THREE.MeshBasicMaterial).opacity = .9 * Math.max(0, fade);
      this.conform(mark.ring);
      mark.ripples.forEach((ripple, i) => {
        const life = (t - i * RIPPLE_GAP) / RIPPLE_LIFE, shown = life > 0 && life < 1;
        ripple.visible = shown;
        if (!shown) return;
        ripple.position.set(x, y + .07, z); ripple.scale.setScalar(1 + life * 1.8);
        (ripple.material as THREE.MeshBasicMaterial).opacity = (1 - life) ** 1.5 * .85 * Math.max(0, fade);
        this.conform(ripple);
      });
      // The glyph hangs over a unit's head, or a little above the ground, and bobs gently.
      const lift = unit ? 2.6 : 1.5;
      mark.group.position.set(x, y, z);
      mark.glyph.position.y = lift + Math.sin(t * 3.2) * .08 + (1 - land) * .4;
      mark.glyph.material.opacity = Math.max(0, fade);
      const positions = mark.motes.geometry.getAttribute('position') as THREE.BufferAttribute, rise = Math.min(1, t / 1.6);
      for (let i = 0; i < MOTES; i++) {
        const spread = 1 + rise * .35;
        positions.setXYZ(i, mark.drift[i * 3] * spread, .1 + mark.drift[i * 3 + 1] * rise, mark.drift[i * 3 + 2] * spread);
      }
      positions.needsUpdate = true;
      (mark.motes.material as THREE.PointsMaterial).opacity = Math.max(0, 1 - t / 1.8) * Math.max(0, fade);
    }
    for (let i = this.live.length - 1; i >= 0; i--) if (this.live[i].age >= PING_SECONDS) this.remove(this.live[i]);
  }

  /** The pings showing now, with their places this frame and ages, for the map chart. */
  marks(): { at: Point; kind: PingKind; mine: boolean; age: number; glyph: HTMLCanvasElement | null }[] {
    return this.live.map(mark => ({ at: { x: mark.at.x, z: mark.at.z }, kind: mark.ping.kind, mine: mark.ping.mine, age: mark.age, glyph: drawn.get(mark.ping.kind) ?? null }));
  }

  clear() { for (let i = this.live.length - 1; i >= 0; i--) this.remove(this.live[i]); }
  dispose() {
    this.clear(); this.warmed();
    for (const parts of this.spare.splice(0)) {
      for (const mesh of [parts.ring, ...parts.ripples]) { mesh.geometry.dispose(); (mesh.material as THREE.Material).dispose(); }
      parts.glyph.material.dispose(); parts.motes.geometry.dispose(); parts.motes.material.dispose();
    }
    for (const texture of this.textures.values()) texture.dispose();
    this.textures.clear(); this.ringGeometry.dispose();
  }

  /** A ping that has run its course leaves the scene and its parts wait for the next one. */
  private remove(mark: Live) {
    this.live.splice(this.live.indexOf(mark), 1);
    this.scene.remove(mark.group, mark.ring, ...mark.ripples);
    mark.glyph.visible = false; mark.glyph.material.opacity = 0;
    this.spare.push(mark);
  }
}

const easeOutBack = (t: number) => { const c = 1.7; return 1 + (c + 1) * (t - 1) ** 3 + c * (t - 1) ** 2; };
